import React from "react";
import FontAwesome from "react-fontawesome";
import {
  isAfter,
  parse,
  addDays,
  differenceInDays,
  set,
  getHours,
  getMinutes,
  parseISO,
} from "date-fns";
import { formattedDateAndTime } from "utils/helperFunctions";
import caretUpImage from "resources/images/caret-up.png";
import caretDownImage from "resources/images/caret-down.png";

export const getAdjustedTimeMultiplierOptions = (
  isAdjustedTimeMultiplierEnabled
) => {
  const regularTimeMulitplierObject = [
    {
      label: `1.0x`,
      value: 1.0,
    },
    {
      label: `1.5x`,
      value: 1.5,
    },
  ];
  const adjustedTimeMultiplierObject = [
    {
      label: `1.0x`,
      value: 1.0,
    },
    {
      label: `1.25x`,
      value: 1.25,
    },
    {
      label: `1.5x`,
      value: 1.5,
    },
    {
      label: `2.0`,
      value: 2.0,
    },
  ];
  return isAdjustedTimeMultiplierEnabled
    ? adjustedTimeMultiplierObject
    : regularTimeMulitplierObject;
};

export const emailHoursDelayOptions = [
  {
    label: "Immediately",
    value: 0,
  },
  {
    label: "12h",
    value: 12,
  },
  {
    label: "24h",
    value: 24,
  },
  {
    label: "48h",
    value: 48,
  },
];

export const adjustedTimeExcludedTests = ["14", "17", "23"]; // MRAB, TypingTest and CAST

export const checkBatteryHasTimedTest = (
  testBatterySubTests,
  testsInBattery,
  testMakerTests,
  fromScheduleTest = false
) => {
  const batteryHasTimedTest =
    testBatterySubTests &&
    testBatterySubTests.valueSeq().reduce((hasTimedTest, test) => {
      let isTimedTest;

      if (fromScheduleTest && testsInBattery) {
        isTimedTest = testsInBattery.get(test.get("testId")).get("timedTest");
      } else if (fromScheduleTest && !testsInBattery) {
        isTimedTest = test.get("timedTest");
      } else {
        isTimedTest =
          testsInBattery &&
          testsInBattery.get(test.get("testId")).first().get("timedTest");
      }

      let isTMTypingTest;

      if (
        test.get("testId").includes("TM") &&
        testMakerTests &&
        testMakerTests.size !== 0
      ) {
        const tmTestIdTrimmed = test.get("testId").substr(2);
        isTMTypingTest =
          testMakerTests.get(tmTestIdTrimmed) &&
          testMakerTests.get(tmTestIdTrimmed).get("isTyping");
      }

      if (
        isTimedTest &&
        !adjustedTimeExcludedTests.includes(test.get("testId")) &&
        !isTMTypingTest
      ) {
        return true;
      }

      return hasTimedTest;
    }, false);
  return batteryHasTimedTest;
};

export const showSendReminder = (
  eventExpirationDays,
  eventExpirationDate,
  eventExpirationTime,
  days
) =>
  (eventExpirationDays && days === 1 && eventExpirationDays > days) ||
  (eventExpirationDays && days !== 1 && eventExpirationDays >= days) ||
  (eventExpirationDate &&
    eventExpirationTime &&
    isAfter(
      parse(
        formattedDateAndTime(eventExpirationDate, eventExpirationTime, true),
        "yyyy-MM-dd h:mm a",
        new Date()
      ),
      addDays(new Date(), days)
    ));

export const getCaretImage = (viewAdvancedSettings) =>
  viewAdvancedSettings ? (
    <img
      src={caretUpImage}
      height="16px"
      width="16px"
      alt="caret symbol facing up"
      style={{ marginBottom: "2px" }}
    />
  ) : (
    <img
      src={caretDownImage}
      height="16px"
      width="16px"
      alt="caret symbol facing down"
      style={{ marginBottom: "2px" }}
    />
  );

export const getEmailTemplateOptions = (emailTemplates) => {
  // Set up options for email templates select button
  const options = [
    {
      value: 0,
      label: "Default Invitation Email Template",
    },
  ];

  emailTemplates
    .filter(
      (template) =>
        (template.get("emailBody").includes("testCenterLink") &&
          template.get("emailBody").includes("eventId")) ||
        template.get("emailBody").includes("candidateTestingLink")
    )
    .sort((a, b) => a.get("emailName").localeCompare(b.get("emailName")))
    .map((template) =>
      options.push({
        value: template.get("emailTemplateId"),
        label: template.get("emailName"),
      })
    );

  return options;
};

export const getUpgradeIcon = (
  showStandardUpgrades,
  showProUpgrades,
  openLearnMore
) =>
  showStandardUpgrades || showProUpgrades ? (
    <FontAwesome
      onClick={openLearnMore}
      name="bolt"
      className="my-auto text-info"
      style={{
        marginLeft: "16px",
        color: "#C0DEDD",
        cursor: "pointer",
      }}
    />
  ) : null;

export const getEventReminderDayOptions = (eventExpirationDays) => {
  // set options for reminder day dropdown
  const eventReminderDayOptions = [];

  // create array 1 to 14 for expiration days.
  // this logic allows us to change the number of days whenever we need to.
  const expirationDayCountArr = Array.from(Array(14).keys(), (n) => n + 1);

  expirationDayCountArr.map((eventExpirationDay) => {
    if (
      eventExpirationDays >= 3 &&
      eventExpirationDays - 2 > 0 &&
      eventExpirationDay <= eventExpirationDays - 2
    ) {
      eventReminderDayOptions.push({
        value: eventExpirationDay,
        label: `${eventExpirationDay} ${
          eventExpirationDay !== 1 ? "days" : "day"
        }`,
      });
    }
  });

  return eventReminderDayOptions;
};

export const getEventReminderDateOptions = (
  eventExpirationDate,
  eventExpirationTime
) => {
  // set options for reminder day dropdown
  const eventReminderDayOptions = [];

  const differenceInDaysBetweenEventExpirationAndToday = getDifferenceInDays(
    eventExpirationDate,
    eventExpirationTime
  );

  // create array based on the difference in days between event expiration date and today.
  // this logic allows us to change the number of days whenever we need to.
  const expirationDayCountArr = Array.from(
    Array(differenceInDaysBetweenEventExpirationAndToday).keys(),
    (n) => n + 1
  );

  expirationDayCountArr.map((eventExpirationDay) => {
    if (
      differenceInDaysBetweenEventExpirationAndToday >= 3 &&
      differenceInDaysBetweenEventExpirationAndToday - 2 > 0 &&
      eventExpirationDay <= differenceInDaysBetweenEventExpirationAndToday - 2
    ) {
      eventReminderDayOptions.push({
        value: eventExpirationDay,
        label: `${eventExpirationDay} ${
          eventExpirationDay !== 1 ? "days" : "day"
        }`,
      });
    }
  });

  return eventReminderDayOptions;
};

export const getDifferenceInDays = (
  eventExpirationDate,
  eventExpirationTime
) => {
  // set the hours and minutes for the eventExpirationDate value using the time parameter value
  const eventExpirationDateWithTime = set(parseISO(eventExpirationDate), {
    hours: getHours(eventExpirationTime),
    minutes: getMinutes(eventExpirationTime),
  });

  const differenceInDaysBetweenEventExpirationAndToday = differenceInDays(
    eventExpirationDateWithTime,
    new Date()
  );

  return differenceInDaysBetweenEventExpirationAndToday;
};
